// src/store/slices/quotationhistory/quotationHistorySlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface QuotationHistoryState {

    // =====================================================
    // 🔹 Table
    // =====================================================

    filteredQuotationList: any[];

    paginator: any;

    pageSize: number;

    // =====================================================
    // 🔹 Loading
    // =====================================================

    loading: boolean;

    // =====================================================
    // 🔹 View
    // =====================================================

    quotationToView: any | null;

    quotationLoaded: boolean;

    // =====================================================
    // 🔹 Modals
    // =====================================================

    showQuotationPdfModal: boolean;

    showQuotationViewModal: boolean;

}

const initialState: QuotationHistoryState = {

    // =====================================================
    // 🔹 Table
    // =====================================================

    filteredQuotationList: [],

    paginator: null,

    pageSize: 10,

    // =====================================================
    // 🔹 Loading
    // =====================================================

    loading: false,

    // =====================================================
    // 🔹 View
    // =====================================================

    quotationToView: null,

    quotationLoaded: false,

    // =====================================================
    // 🔹 Modals
    // =====================================================

    showQuotationPdfModal: false,

    showQuotationViewModal: false,

};

const quotationHistorySlice = createSlice({

    name: 'quotationHistory',

    initialState,

    reducers: {

        // =====================================================
        // 🔹 Loading
        // =====================================================

        setLoading(
            state,
            action: PayloadAction<boolean>
        ) {

            state.loading = action.payload;

        },

        // =====================================================
        // 🔹 Table
        // =====================================================

        setFilteredQuotationList(
            state,
            action: PayloadAction<any>
        ) {

            state.filteredQuotationList =
                action.payload.content;

            state.paginator =
                action.payload;

        },

        clearFilteredQuotationList(
            state
        ) {

            state.filteredQuotationList = [];

            state.paginator = null;

        },

        setPageSize(
            state,
            action: PayloadAction<number>
        ) {

            state.pageSize =
                action.payload;

        },

        // =====================================================
        // 🔹 View
        // =====================================================

        setQuotationToView(
            state,
            action: PayloadAction<any | null>
        ) {

            state.quotationToView =
                action.payload;

        },

        setQuotationLoaded(
            state,
            action: PayloadAction<boolean>
        ) {

            state.quotationLoaded =
                action.payload;

        },

        clearQuotationView(
            state
        ) {

            state.quotationToView = null;

            state.quotationLoaded = false;

        },

        // =====================================================
        // 🔹 PDF Modal
        // =====================================================

        setOpenQuotationPdfModal(
            state
        ) {

            state.showQuotationPdfModal = true;

        },

        setCloseQuotationPdfModal(
            state
        ) {

            state.showQuotationPdfModal = false;

        },

        // =====================================================
        // 🔹 View Modal
        // =====================================================

        setOpenQuotationViewModal(
            state
        ) {

            state.showQuotationViewModal = true;

        },

        setCloseQuotationViewModal(
            state
        ) {

            state.showQuotationViewModal = false;

        },

        // =====================================================
        // 🔹 Remove From List
        // =====================================================

        removeQuotationFromList(
            state,
            action: PayloadAction<number>
        ) {

            state.filteredQuotationList =
                state.filteredQuotationList.filter(
                    quotation =>
                        quotation.id !== action.payload
                );

        },

        // =====================================================
        // 🔹 Update Status
        // =====================================================

        updateQuotationStatus(
            state,
            action: PayloadAction<{
                id: number;
                status: string;
            }>
        ) {

            const {
                id,
                status
            } = action.payload;

            const index =
                state.filteredQuotationList.findIndex(
                    quotation =>
                        quotation.id === id
                );

            if (index !== -1) {

                state.filteredQuotationList[index].status =
                    status;

            }

        },

    },

});

export const {

    // =====================================================
    // 🔹 Loading
    // =====================================================

    setLoading,

    // =====================================================
    // 🔹 Table
    // =====================================================

    setFilteredQuotationList,

    clearFilteredQuotationList,

    setPageSize,

    // =====================================================
    // 🔹 View
    // =====================================================

    setQuotationToView,

    setQuotationLoaded,

    clearQuotationView,

    // =====================================================
    // 🔹 PDF Modal
    // =====================================================

    setOpenQuotationPdfModal,

    setCloseQuotationPdfModal,

    // =====================================================
    // 🔹 View Modal
    // =====================================================

    setOpenQuotationViewModal,

    setCloseQuotationViewModal,

    // =====================================================
    // 🔹 Actions
    // =====================================================

    removeQuotationFromList,

    updateQuotationStatus,

} = quotationHistorySlice.actions;

export default quotationHistorySlice.reducer;